import axios from 'axios';
import { contextoComoTexto } from '../utils/supportContext';

/**
 * Tickets de soporte contra el Reporter (soporte.consoltechsv.com).
 *
 * POR QUE SE ENVIA DESDE EL NAVEGADOR y no desde nuestro backend:
 * el Reporter limita a 5 creaciones cada 15 minutos POR IP. Si el backend de
 * talleres reenviara los tickets, los talleres compartirian la IP del servidor
 * y el sexto reporte fallaria — justo durante una caida, que es cuando todos
 * reportan a la vez. Enviando desde el navegador, cada persona gasta su propio
 * cupo, que es como el Reporter fue disenado.
 *
 * Contrapartida asumida: el Reporter recibe la identidad tal como la manda el
 * navegador. Para un ticket de soporte el riesgo es menor que quedarse sin
 * poder reportar.
 */

const API = 'https://api.soporte.consoltechsv.com/api/publico';

// Clave del aplicativo en el catalogo del Reporter. El id es un ObjectId que
// puede cambiar si se resiembra, por eso se resuelve por clave y no se fija.
const CLAVE_APP = 'talleres';

export const TIPOS_TICKET = [
  // El valor DEBE ser uno del enum del Reporter: error | mejora | consulta.
  { value: 'error', label: 'Algo no funciona' },
  { value: 'consulta', label: 'Tengo una duda' },
  { value: 'mejora', label: 'Sugerencia o mejora' },
];

// El catalogo casi nunca cambia: se resuelve una vez por carga de pagina.
let appIdCache = null;

const resolverAplicativo = async () => {
  if (appIdCache) return appIdCache;
  const { data } = await axios.get(`${API}/catalogo`, { timeout: 15000 });
  const app = (data?.aplicativos || []).find((a) => a.clave === CLAVE_APP);
  if (!app?.id) throw new Error('No se pudo identificar el sistema en soporte');
  appIdCache = app.id;
  return appIdCache;
};

/**
 * Descripcion final: lo que escribio la persona + el contexto tecnico.
 * El Reporter no tiene campo de metadatos, asi que este es el unico lugar
 * donde el contexto viaja completo y legible para quien atiende.
 */
const armarDescripcion = (descripcion, contexto) =>
  `${descripcion}\n\n--- Contexto técnico (automático) ---\n${contextoComoTexto(contexto)}`;

const supportService = {
  crearTicket: async ({ tipo, titulo, descripcion, contexto }) => {
    const aplicativo = await resolverAplicativo();

    const payload = {
      aplicativo,
      // El Reporter exige nombre y correo de quien reporta.
      nombre: contexto?.usuario?.nombre || 'Usuario del sistema',
      correo: contexto?.usuario?.correo || '',
      // Texto libre: es el unico canal para decir de que taller viene. El
      // agente lo enlaza al cliente real desde su panel.
      empresa: contexto?.taller?.nombre || '',
      tipo,
      titulo: titulo.slice(0, 140), // el Reporter corta en 140, no en 160
      descripcion: armarDescripcion(descripcion, contexto),
      entorno: {
        mismoDispositivo: true,
        detectado: {
          tipo: contexto?.tipoEquipo || '',
          sistema: contexto?.sistemaOperativo || '',
          navegador: contexto?.navegador || '',
          pantalla: contexto?.pantallaTamano || '',
          agente: navigator.userAgent,
        },
      },
      // ⚠️ NUNCA agregar `refInterna`: es el honeypot del Reporter. Si viaja
      // con cualquier valor, responde 201 con un codigo FALSO y no guarda nada.
    };

    try {
      const { data } = await axios.post(`${API}/tickets`, payload, { timeout: 20000 });
      return {
        numero: data?.codigo || null,
        token: data?.token || null,
        url: data?.token ? `https://soporte.consoltechsv.com/ticket/${data.token}` : null,
      };
    } catch (err) {
      const status = err.response?.status;
      const mensaje = err.response?.data?.mensaje;

      // Los limites del Reporter se explican en cristiano: un "429" a secas
      // deja al usuario sin saber si reintentar o a quien llamar.
      if (status === 429) {
        throw new Error(
          mensaje || 'Envió varios reportes seguidos. Espere unos minutos e intente de nuevo.'
        );
      }
      if (status === 409) {
        throw new Error(mensaje || 'Ya envió un reporte igual hace unos minutos.');
      }
      throw new Error(mensaje || 'No se pudo enviar el ticket. Intente de nuevo.');
    }
  },
};

export default supportService;
